import { TransactionLink } from '@rentguru/commons-utils';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { PENDING_FAST_VALIDATIONS_CACHE } from './cacheKeys';

export interface PendingFastValidation {
  transactionId: string;
  clientId: string;
  amount: number;
  invoiceIds: string[];
  postings: { id: string; amountSelected: number }[];
  linksToWrite: TransactionLink[] | null;
  linksToRestore: TransactionLink[];
  replayAttempts: number;
  scheduledAt: number;
}

type PendingFastValidations = Record<string, PendingFastValidation>;

const readAll = (): PendingFastValidations => {
  try {
    const stored = window.localStorage.getItem(PENDING_FAST_VALIDATIONS_CACHE);
    if (isNil(stored)) {
      return {};
    }
    const parsed = JSON.parse(stored);
    if (isNil(parsed) || Array.isArray(parsed) || typeof parsed !== 'object') {
      return {};
    }
    return parsed as PendingFastValidations;
  } catch {
    return {};
  }
};

const writeAll = (pendingFastValidations: PendingFastValidations) => {
  try {
    window.localStorage.setItem(PENDING_FAST_VALIDATIONS_CACHE, JSON.stringify(pendingFastValidations));
  } catch {
    return;
  }
};

export const rememberPendingFastValidation = (pendingFastValidation: PendingFastValidation) => {
  writeAll({ ...readAll(), [pendingFastValidation.transactionId]: pendingFastValidation });
};

export const forgetPendingFastValidation = (transactionId: string) => {
  const pendingFastValidations = readAll();
  if (isNil(pendingFastValidations[transactionId])) {
    return;
  }
  delete pendingFastValidations[transactionId];
  writeAll(pendingFastValidations);
};

const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const readPendingFastValidations = (clientId: string): PendingFastValidation[] => {
  const now = Date.now();
  const pendingFastValidations = readAll();
  const expiredIds = Object.values(pendingFastValidations).reduce<string[]>((accumulator, pendingFastValidation) => {
    if (now - pendingFastValidation.scheduledAt >= MAX_AGE_MS) {
      accumulator.push(pendingFastValidation.transactionId);
    }
    return accumulator;
  }, []);
  if (!isEmpty(expiredIds)) {
    expiredIds.forEach((transactionId) => delete pendingFastValidations[transactionId]);
    writeAll(pendingFastValidations);
  }
  return Object.values(pendingFastValidations).filter(
    (pendingFastValidation) => pendingFastValidation.clientId === clientId
  );
};
